import { ErrorBoundary } from '@common/components/feedback/ErrorBoundary';
import { padroesDoIdioma } from '@common/i18n/formatosData';
import { isDev } from '@config/env';
import i18n from '@config/i18n';
import { queryClient } from '@config/queryClient';
import { DatePickerProvider, TableFilterProvider, ToastProvider } from '@ntconsult/aluma-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { enUS, es, ptBR } from 'date-fns/locale';
import type { ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

// `locale` do DatePicker é um `Locale` do date-fns, não a string do i18n.
const LOCALES = { 'pt-BR': ptBR, 'en-US': enUS, 'es-ES': es } as const;

function DatePickers({ children }: { children: ReactNode }) {
  const { t, i18n: instancia } = useTranslation('common');
  const locale = LOCALES[instancia.language as keyof typeof LOCALES] ?? ptBR;
  // O formato, o primeiro dia da semana e o relógio seguem o idioma escolhido, como o texto
  // das telas (`formatosData.ts`): sem isso o seletor ficaria sempre em dd/MM/yyyy.
  const padroes = padroesDoIdioma(instancia.language);
  return (
    <DatePickerProvider
      locale={locale}
      format={padroes.dia}
      dateTimeFormat={`${padroes.dia} ${padroes.hora}`}
      timeFormat={padroes.hora}
      hourCycle={padroes.ciclo}
      weekStartsOn={locale.options?.weekStartsOn}
      // Rótulos e placeholders dos segmentos do `DateField` (spec 26 R3); sem isto ficam em pt-BR.
      messages={{
        day: t('data.dia'),
        month: t('data.mes'),
        year: t('data.ano'),
        dayPlaceholder: t('data.diaPlaceholder'),
        monthPlaceholder: t('data.mesPlaceholder'),
        yearPlaceholder: t('data.anoPlaceholder'),
      }}
    >
      {children}
    </DatePickerProvider>
  );
}

/** Textos dos filtros de coluna da `Table` no idioma ativo; sem isto ficam fixos em pt-BR. */
function TabelaFiltros({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <TableFilterProvider
      messages={{
        trigger: (column, active) =>
          active
            ? t('tableFilters.triggerActive', { column })
            : t('tableFilters.trigger', { column }),
        title: (column) => t('tableFilters.title', { column }),
        apply: t('tableFilters.apply'),
        clear: t('tableFilters.clear'),
        textPlaceholder: t('tableFilters.textPlaceholder'),
        textInputLabel: (column) => t('tableFilters.textInputLabel', { column }),
        optionsSearchPlaceholder: t('tableFilters.optionsSearchPlaceholder'),
        optionsSearchLabel: (column) => t('tableFilters.optionsSearchLabel', { column }),
        optionsEmpty: (query) => t('tableFilters.optionsEmpty', { query }),
        min: t('tableFilters.min'),
        max: t('tableFilters.max'),
      }}
    >
      {children}
    </TableFilterProvider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <DatePickers>
            <TabelaFiltros>
              <ToastProvider>{children}</ToastProvider>
            </TabelaFiltros>
          </DatePickers>
        </I18nextProvider>
        {isDev && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
